import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  alpha,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Warning,
  CheckCircle,
  Event,
  Speed,
  Description,
  Security,
  CloudUpload,
  AccountBalance,
  HelpOutline
} from '@mui/icons-material';

const Reforme2026 = () => {
  const navigate = useNavigate();

  return (
    <Box sx={(theme) => ({ minHeight: '100vh', background: theme.palette.background.default })}>
      {/* Hero Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #D7DCE4 0%, #C7CFDA 100%)',
        color: '#1F2937',
        py: 8
      }}>
        <Container maxWidth="lg">
          <Button 
            startIcon={<ArrowBack />} 
            sx={{ color: '#1F2937', mb: 3 }}
            onClick={() => navigate('/')}
          >
            Retour à l'accueil
          </Button>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Warning sx={{ fontSize: 64 }} />
            <Typography variant="h2" fontWeight={800}>
              Réforme 2026 : Facture Électronique Obligatoire
            </Typography>
          </Stack>
          <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '900px' }}>
            Tout ce que vous devez savoir pour être prêt, sans stress et en toute conformité.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        
        {/* Section : C'est quoi ? */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={700} mb={3} color="#1F2937">
            📋 C'est quoi exactement cette réforme ?
          </Typography>
          <Card sx={{ p: 4, bgcolor: alpha('#F1F5F9', 0.9), border: '2px solid #DCE1E8' }}>
            <CardContent>
              <Typography variant="h5" fontWeight={600} mb={2}>
                À partir de 2026, la facture électronique devient <strong>OBLIGATOIRE</strong> pour toutes les entreprises françaises.
              </Typography>
              <Typography variant="body1" fontSize="1.1rem" lineHeight={1.8} mb={2}>
                Cela signifie que :
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#64748B', mt: 0.5 }} />
                  <Typography variant="body1">
                    Vous devez <strong>émettre vos factures</strong> au format électronique structuré (pas juste un PDF)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#64748B', mt: 0.5 }} />
                  <Typography variant="body1">
                    Vous devez <strong>recevoir les factures</strong> de vos fournisseurs en électronique
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#64748B', mt: 0.5 }} />
                  <Typography variant="body1">
                    Les factures doivent être <strong>signées électroniquement</strong> et immuables
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#64748B', mt: 0.5 }} />
                  <Typography variant="body1">
                    Vous devez utiliser une <strong>plateforme certifiée</strong> (comme Facteio)
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Section : Pourquoi ? */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={700} mb={3} color="#1F2937">
            🤔 Pourquoi cette obligation ?
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', p: 3 }}>
                <CardContent>
                  <AccountBalance sx={{ fontSize: 48, color: '#64748B', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Lutter contre la fraude fiscale
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    L'État veut tracer toutes les transactions pour limiter les fraudes à la TVA (estimées à 20 milliards d'euros/an).
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', p: 3 }}>
                <CardContent>
                  <Speed sx={{ fontSize: 48, color: '#64748B', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Simplifier la gestion
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    À terme, moins de papier, moins d'erreurs, rapprochement automatique banque/factures.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', p: 3 }}>
                <CardContent>
                  <CloudUpload sx={{ fontSize: 48, color: '#64748B', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Moderniser les échanges
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Harmonisation européenne, échanges B2B plus rapides, pré-remplissage automatique des déclarations.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Section : Timeline */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={700} mb={3} color="#1F2937">
            📅 Calendrier de mise en œuvre
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', bgcolor: alpha('#F1F5F9', 0.9), border: '1px solid #DCE1E8' }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event sx={{ color: '#64748B' }} />
                    <Typography variant="h6" fontWeight={700}>1er septembre 2026</Typography>
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600}>Grandes entreprises</Typography>
                  <Typography variant="body2">
                    Obligation d'émettre et recevoir des factures électroniques
                  </Typography>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', bgcolor: alpha('#F1F5F9', 0.9), border: '1px solid #DCE1E8' }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event sx={{ color: '#64748B' }} />
                    <Typography variant="h6" fontWeight={700}>1er septembre 2027</Typography>
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600}>PME et ETI</Typography>
                  <Typography variant="body2">
                    Extension à toutes les entreprises de taille intermédiaire
                  </Typography>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', bgcolor: alpha('#F1F5F9', 0.9), border: '1px solid #DCE1E8' }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event sx={{ color: '#64748B' }} />
                    <Typography variant="h6" fontWeight={700}>1er septembre 2027</Typography>
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600}>Micro-entreprises & Indépendants</Typography>
                  <Typography variant="body2">
                    <strong>C'est pour vous !</strong> Tous les auto-entrepreneurs et TPE doivent être conformes.
                  </Typography>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Section : Que faire ? */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={700} mb={3} color="#1F2937">
            ✅ Que devez-vous faire concrètement ?
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', bgcolor: alpha('#F1F5F9', 0.9) }}>
                <Typography variant="h5" fontWeight={600} mb={2} color="#1F2937">
                  1️⃣ Choisir une plateforme certifiée
                </Typography>
                <Typography variant="body1" mb={2}>
                  Vous ne pouvez pas envoyer vos factures "comme avant". Vous devez passer par une <strong>plateforme agréée</strong> qui :
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CheckCircle sx={{ color: '#64748B', fontSize: 20 }} />
                    <Typography variant="body2">Génère des factures au format structuré (XML/Factur-X)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CheckCircle sx={{ color: '#64748B', fontSize: 20 }} />
                    <Typography variant="body2">Signe électroniquement vos factures</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CheckCircle sx={{ color: '#64748B', fontSize: 20 }} />
                    <Typography variant="body2">Transmet automatiquement à l'administration fiscale</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CheckCircle sx={{ color: '#64748B', fontSize: 20 }} />
                    <Typography variant="body2">Archive vos documents de façon sécurisée</Typography>
                  </Box>
                </Stack>
                <Box sx={{ mt: 3, p: 2, bgcolor: alpha('#E5E8EE', 0.9), borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    👉 Facteio est conforme et certifié pour la réforme 2026
                  </Typography>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', bgcolor: alpha('#F1F5F9', 0.9) }}>
                <Typography variant="h5" fontWeight={600} mb={2} color="#1F2937">
                  2️⃣ Adapter vos processus
                </Typography>
                <Typography variant="body1" mb={2}>
                  Quelques ajustements simples dans votre quotidien :
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CheckCircle sx={{ color: '#64748B', fontSize: 20 }} />
                    <Typography variant="body2">Créer vos factures dans Facteio (au lieu de Word/Excel)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CheckCircle sx={{ color: '#64748B', fontSize: 20 }} />
                    <Typography variant="body2">Envoyer vos factures via la plateforme (automatique)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CheckCircle sx={{ color: '#64748B', fontSize: 20 }} />
                    <Typography variant="body2">Recevoir et traiter les factures fournisseurs en ligne</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CheckCircle sx={{ color: '#64748B', fontSize: 20 }} />
                    <Typography variant="body2">Conserver les archives électroniques (plus besoin de papier !)</Typography>
                  </Box>
                </Stack>
                <Box sx={{ mt: 3, p: 2, bgcolor: alpha('#E5E8EE', 0.9), borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    💡 Avec Facteio, tout est automatique. Vous créez, on s'occupe du reste.
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Section : Risques */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={700} mb={3} color="#1F2937">
            ⚠️ Que se passe-t-il si je ne suis pas conforme ?
          </Typography>
          <Card sx={{ p: 4, bgcolor: alpha('#F1F5F9', 0.9), border: '2px solid #DCE1E8' }}>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="#64748B" mb={1}>
                    ❌ Amendes et pénalités
                  </Typography>
                  <Typography variant="body1">
                    Jusqu'à 15€ par facture non conforme, avec un plafond de 15 000€ par an. Pour une TPE, ça peut faire très mal.
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="h6" fontWeight={600} color="#64748B" mb={1}>
                    ❌ Redressement fiscal
                  </Typography>
                  <Typography variant="body1">
                    Si vos factures ne sont pas conformes, elles peuvent être rejetées par l'administration = TVA non déductible.
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="h6" fontWeight={600} color="#64748B" mb={1}>
                    ❌ Blocage des paiements
                  </Typography>
                  <Typography variant="body1">
                    Certains clients (notamment les grandes entreprises) refuseront de payer des factures non électroniques.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Section : Facteio vous aide */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={700} mb={3} color="#1F2937">
            🧘 Comment Facteio vous aide à être conforme ?
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', bgcolor: alpha('#F1F5F9', 0.9) }}>
                <Security sx={{ fontSize: 48, color: '#64748B', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} mb={2}>
                  100% Conforme dès le départ
                </Typography>
                <Typography variant="body2">
                  Format structuré, signature électronique, immutabilité, archivage sécurisé. Tout est géré automatiquement.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', bgcolor: alpha('#F1F5F9', 0.9) }}>
                <Description sx={{ fontSize: 48, color: '#64748B', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Interface simple, sans jargon
                </Typography>
                <Typography variant="body2">
                  Vous créez vos factures comme d'habitude. Facteio s'occupe de toute la partie technique et légale.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', bgcolor: alpha('#F1F5F9', 0.9) }}>
                <HelpOutline sx={{ fontSize: 48, color: '#64748B', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Accompagnement humain
                </Typography>
                <Typography variant="body2">
                  Besoin d'aide ? Je vous explique la réforme et paramètre votre compte avec vous. Sans stress.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Final */}
        <Box 
          sx={{ 
            p: 6, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1F2937 0%, #4B5563 100%)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Typography variant="h3" fontWeight={800} mb={2}>
            Ne laissez pas la réforme 2026 vous stresser.
          </Typography>
          <Typography variant="h6" mb={4} sx={{ opacity: 0.9 }}>
            Soyez prêt dès aujourd'hui avec Facteio. Simple, conforme, serein.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                bgcolor: 'white', 
                color: '#1F2937',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 700,
                '&:hover': { bgcolor: '#f1f1f1' }
              }}
              onClick={() => navigate('/register')}
            >
              Essayer gratuitement
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                borderWidth: 2,
                '&:hover': { borderWidth: 2, bgcolor: alpha('#fff', 0.1) }
              }}
              onClick={() => navigate('/faq')}
            >
              Voir la FAQ
            </Button>
          </Stack>
        </Box>

      </Container>
    </Box>
  );
};

export default Reforme2026;
